/**
 * My payslips.
 *
 * Only finalised payslips reach this screen, by contract. A draft pay run is
 * the boss's working copy and a figure that later changes is worse than no
 * figure at all.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Printer, Receipt } from 'lucide-react';
import { api, qk, type Payslip } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { fmtDateRange, fmtMoney } from '../../lib/time.ts';
import { EmptyState, ErrorState, LoadingBlock } from '../../components/Bits.tsx';
import { Sheet } from '../../components/Overlay.tsx';
import { PayslipPrintArea, PayslipSheet } from '../../components/PayslipSheet.tsx';

export default function MyPayslipsPage() {
  const { tenant, me } = useSession();
  const slug = tenant?.tenantSlug ?? '';
  const [open, setOpen] = useState<Payslip | null>(null);

  const query = useQuery({
    queryKey: qk.myPayslips(slug),
    queryFn: () => api.myPayslips(slug),
    enabled: slug !== '',
  });

  if (query.isLoading) return <LoadingBlock label="Loading your payslips" />;
  if (query.isError) {
    return <ErrorState error={query.error} what="your payslips" onRetry={() => void query.refetch()} />;
  }

  const slips = [...(query.data?.items ?? [])].sort((a, b) =>
    (b.periodStart ?? '').localeCompare(a.periodStart ?? ''),
  );
  const name = me?.user.name ?? 'You';

  return (
    <div className="px-4 py-4">
      <header className="mb-4">
        <h1 className="text-[20px] font-extrabold tracking-tight text-ink">Payslips</h1>
        <p className="text-[13px] text-muted">
          {slips.length === 0 ? 'Nothing yet' : `${slips.length} ${slips.length === 1 ? 'payslip' : 'payslips'}`}
        </p>
      </header>

      {slips.length === 0 ? (
        <EmptyState
          icon={<Receipt size={20} aria-hidden="true" />}
          title="No payslips yet"
          body="Your first payslip appears here once your manager finalises a pay run that includes you."
        />
      ) : (
        <div className="card overflow-hidden">
          {slips.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setOpen(s)}
              className="row-link press flex w-full items-center gap-3 px-4 py-3.5 text-left"
              style={{ borderTop: i > 0 ? '1px solid var(--sp-hairline)' : undefined }}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-bold text-ink">
                  {s.periodStart && s.periodEnd ? fmtDateRange(s.periodStart, s.periodEnd) : 'Pay period'}
                </span>
                <span className="tabular block truncate text-[12.5px] text-muted">
                  {fmtMoney(s.gross)} gross, {fmtMoney(s.tax)} tax
                </span>
              </span>
              <span className="tabular shrink-0 text-right">
                <span className="block text-[15px] font-extrabold" style={{ color: 'var(--sp-accent-strong)' }}>
                  {fmtMoney(s.net)}
                </span>
                <span className="block text-[11px] text-faint">in your account</span>
              </span>
              <ChevronRight size={16} aria-hidden="true" className="row-chev" style={{ color: 'var(--sp-text-faint)' }} />
            </button>
          ))}
        </div>
      )}

      <Sheet
        open={open !== null}
        onClose={() => setOpen(null)}
        title="Payslip"
        subtitle={open?.periodStart && open?.periodEnd ? fmtDateRange(open.periodStart, open.periodEnd) : undefined}
        footer={
          <button type="button" className="btn btn-lg w-full" onClick={() => window.print()}>
            <Printer size={16} aria-hidden="true" />
            Save or print a copy
          </button>
        }
      >
        {open ? (
          <PayslipSheet slip={{ ...open, tenantName: open.tenantName ?? tenant?.tenantName }} staffName={name} />
        ) : null}
      </Sheet>

      <PayslipPrintArea
        slip={open ? { ...open, tenantName: open.tenantName ?? tenant?.tenantName } : null}
        staffName={name}
      />
    </div>
  );
}
